import {
  ListTable,
  ListTableBody,
  ListTableCell,
  ListTableHead,
  ListTableHeader,
  ListTableRow,
} from "@coval/ui"

import type { RunMetricOutput } from "@/types/RunMetricOutput"

function customStats(
  mo: RunMetricOutput,
  name: string,
): Record<string, unknown> | undefined {
  const stats: unknown = mo.result?.stats
  if (!Array.isArray(stats)) {
    return undefined
  }
  const stat: unknown = stats.find(
    (item: unknown) =>
      typeof item === "object" &&
      item !== null &&
      "name" in item &&
      item.name === name,
  )
  if (typeof stat !== "object" || stat === null || !("values" in stat)) {
    return undefined
  }
  const values = stat.values
  return typeof values === "object" && values !== null && !Array.isArray(values)
    ? (values as Record<string, unknown>)
    : undefined
}

const displayValue = (value: string) =>
  value === "" ? "(empty string)" : value
const offset = (seconds: number) => `${seconds.toFixed(3)} s`

export function CustomTraceValues({ mo }: { mo: RunMetricOutput }) {
  const counts = customStats(mo, "trace_value_counts")
  const rawOccurrences: unknown[] = mo.subvalues_by_timestamp ?? []
  const occurrences = rawOccurrences.filter(
    (
      item,
    ): item is {
      start_offset: number
      end_offset: number
      string_value: string
    } =>
      typeof item === "object" &&
      item !== null &&
      "output_type" in item &&
      item.output_type === "string" &&
      "string_value" in item &&
      typeof item.string_value === "string" &&
      "start_offset" in item &&
      typeof item.start_offset === "number" &&
      Number.isFinite(item.start_offset) &&
      "end_offset" in item &&
      typeof item.end_offset === "number" &&
      Number.isFinite(item.end_offset),
  )
  const unavailableOccurrences = rawOccurrences.length - occurrences.length
  const total = customStats(mo, "trace_value_occurrences")?.total
  const frequencies = counts
    ? Object.entries(counts).filter(
        (entry): entry is [string, number] => typeof entry[1] === "number",
      )
    : []

  return (
    <div
      className="space-y-4 text-sm"
      data-dd-privacy="mask"
      data-ph-capture="false"
    >
      <section aria-label="Unique trace values">
        <h3 className="mb-2 font-medium text-coval-text">
          Values and frequencies
        </h3>
        {frequencies.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {frequencies.map(([value, count]) => (
              <span
                key={value}
                className="max-w-full break-words rounded border border-coval-border bg-surface-1 px-2 py-1"
              >
                <span className="whitespace-pre-wrap font-mono">
                  {displayValue(value)}
                </span>{" "}
                <span className="text-coval-text-muted">× {count}</span>
              </span>
            ))}
          </div>
        ) : (
          <div className="text-coval-text-muted">
            Frequencies are unavailable for this result.
          </div>
        )}
      </section>
      <section aria-label="Trace value occurrences">
        <h3 className="font-medium text-coval-text">Occurrences</h3>
        <p className="mb-2 text-xs text-coval-text-muted">
          Times are seconds from the first span matching the span name and
          attribute, before trace filters.
        </p>
        {unavailableOccurrences > 0 && (
          <p className="mb-2 text-coval-text-muted">
            {unavailableOccurrences} occurrence
            {unavailableOccurrences === 1 ? "" : "s"} could not be displayed
            because timing or value is unavailable.
          </p>
        )}
        {typeof total === "number" && total > occurrences.length && (
          <p className="mb-2 text-coval-text-muted">
            Showing {occurrences.length} of {total} occurrences. Frequencies
            include all matches.
          </p>
        )}
        {occurrences.length > 0 ? (
          <div className="max-h-80 overflow-auto rounded border border-coval-border">
            <ListTable>
              <ListTableHeader>
                <ListTableRow>
                  <ListTableHead>Start</ListTableHead>
                  <ListTableHead>End</ListTableHead>
                  <ListTableHead>Value</ListTableHead>
                </ListTableRow>
              </ListTableHeader>
              <ListTableBody>
                {occurrences.map((item, index) => (
                  <ListTableRow key={`${item.start_offset}-${index}`}>
                    <ListTableCell className="whitespace-nowrap tabular-nums">
                      {offset(item.start_offset)}
                    </ListTableCell>
                    <ListTableCell className="whitespace-nowrap tabular-nums">
                      {offset(item.end_offset)}
                    </ListTableCell>
                    <ListTableCell className="break-words whitespace-pre-wrap font-mono">
                      {displayValue(item.string_value ?? "")}
                    </ListTableCell>
                  </ListTableRow>
                ))}
              </ListTableBody>
            </ListTable>
          </div>
        ) : (
          <p className="text-coval-text-muted">
            No timed occurrences are available for this result.
          </p>
        )}
      </section>
    </div>
  )
}

export function CustomTraceValuesSummary({ mo }: { mo: RunMetricOutput }) {
  const values = mo.set_value ?? []
  return (
    <span data-dd-privacy="mask" data-ph-capture="false">
      {values.length
        ? values.slice(0, 3).map(displayValue).join(", ")
        : "No values"}
      {values.length > 3 ? ` +${values.length - 3}` : ""}
    </span>
  )
}
